/** @jsxImportSource @emotion/react */
import { useMemo, useState, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
    Legend,
} from "recharts";
import { Text } from "@phoenix/components";
import {
    ChartTooltip,
    ChartTooltipItem,
    defaultCartesianGridProps,
    defaultLegendProps,
    defaultXAxisProps,
    defaultYAxisProps,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import type { TracesByTimeSummaryQuery } from "./__generated__/TracesByTimeSummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const tabsHeaderCSS = css`
  display: flex;
  gap: 12px;
  background: var(--ac-global-color-grey-50, rgba(0,0,0,0.03));
  padding: 4px;
  border-radius: 12px;
  margin-bottom: 24px;
  width: fit-content;
`;

const tabBtnCSS = (active: boolean) => css`
  padding: 8px 16px;
  border: none;
  background: ${active ? "var(--ac-global-background-color-dark)" : "transparent"};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  color: ${active ? "var(--ac-global-color-primary)" : "var(--ac-global-text-color-400)"};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: ${active ? "0 2px 10px rgba(0,0,0,0.08)" : "none"};

  &:hover {
    color: var(--ac-global-color-primary);
    background: ${active ? "var(--ac-global-background-color-dark)" : "rgba(0,0,0,0.05)"};
  }
`;

const chartHeaderCSS = css`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const bigNumberCSS = css`
  font-size: 42px;
  font-weight: 900;
  color: var(--ac-global-text-color-900);
  line-height: 1;
  letter-spacing: -1.5px;
`;

const iconWrapperCSS = (color: string) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${color}15;
  color: ${color};
  font-size: 24px;
`;

const subtitleCSS = css`
  font-size: 13px;
  font-weight: 600;
  color: var(--ac-global-text-color-500);
  margin-left: 4px;
  opacity: 0.7;
`;

const chartContainerCSS = css`
  flex: 1;
  min-height: 280px;
`;

const TAB_TRACES = "traces";
const TAB_OBSERVATIONS = "observations";

function CustomTooltip({
    active,
    payload,
    label,
    scale,
}: TooltipContentProps<number, string> & { scale?: string }) {
    if (active && payload && payload.length) {
        const date = new Date(label || "");
        const isDayScale = scale === "DAY";
        const timeStr = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(isDayScale ? {} : { hour: '2-digit', minute: '2-digit', hour12: true })
        });

        return (
            <ChartTooltip>
                {label && (
                    <Text weight="heavy" size="S" color="secondary">{timeStr}</Text>
                )}
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {payload.map((item, idx) => (
                        <ChartTooltipItem
                            key={idx}
                            color={item.color}
                            shape="circle"
                            name={item.name || ""}
                            value={intShortFormatter(item.value ?? 0)}
                        />
                    ))}
                </div>
            </ChartTooltip>
        );
    }
    return null;
}

export function TracesByTimeSummary({ projectId, timeRange, widgetId, widgetTitle, onDataExport }: ProjectMetricViewProps) {
    const [activeTab, setActiveTab] = useState(TAB_TRACES);
    const defaultScale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    // Machi, respect the aggregation! Week/Month/Day (5-35 days) should stay as DAY for cleaner views
    const scale = useMemo(() => {
        if (defaultScale === "MONTH" || defaultScale === "WEEK" || defaultScale === "DAY") return "DAY";
        return defaultScale;
    }, [defaultScale]);

    const data = useLazyLoadQuery<TracesByTimeSummaryQuery>(
        graphql`
      query TracesByTimeSummaryQuery(
        $projectId: ID!
        $timeRange: TimeRange!
        $timeBinConfig: TimeBinConfig!
      ) {
        project: node(id: $projectId) {
          ... on Project {
            traceCountByStatusTimeSeries(
              timeRange: $timeRange
              timeBinConfig: $timeBinConfig
            ) {
              data {
                timestamp
                okCount
                errorCount
              }
            }
            spanCountTimeSeries(
              timeRange: $timeRange
              timeBinConfig: $timeBinConfig
            ) {
              data {
                timestamp
                okCount
                errorCount
                unsetCount
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: {
                start: timeRange.start?.toISOString(),
                end: timeRange.end?.toISOString(),
            },
            timeBinConfig: {
                scale,
                utcOffsetMinutes,
            },
        }
    );

    const { tracesChartData, totalTraces } = useMemo(() => {
        const raw = data.project?.traceCountByStatusTimeSeries?.data ?? [];
        let total = 0;
        const chart = raw.map((d) => {
            const count = (d.okCount || 0) + (d.errorCount || 0);
            total += count;
            return { timestamp: d.timestamp, value: count };
        });

        // Machi, Smart Cropping! 🚀
        const firstIdx = chart.findIndex(d => d.value > 0);
        const lastIdx = chart.length - [...chart].reverse().findIndex(d => d.value > 0);

        const cropped = (firstIdx === -1) ? chart : chart.slice(
            Math.max(0, firstIdx - 4), // 4 point buffer for context
            Math.min(chart.length, lastIdx + 4)
        );

        return { tracesChartData: cropped, totalTraces: total };
    }, [data.project?.traceCountByStatusTimeSeries?.data]);

    const { obsChartData, totalObs } = useMemo(() => {
        const raw = data.project?.spanCountTimeSeries?.data ?? [];
        let total = 0;
        const chart = raw.map((d) => {
            const sum = (d.okCount || 0) + (d.errorCount || 0) + (d.unsetCount || 0);
            total += sum;
            return {
                timestamp: d.timestamp,
                Default: d.okCount || 0,
                Debug: d.unsetCount || 0,
                Error: d.errorCount || 0,
                _total: sum
            };
        });

        const firstIdx = chart.findIndex(d => d._total > 0);
        const lastIdx = chart.length - [...chart].reverse().findIndex(d => d._total > 0);

        const cropped = (firstIdx === -1) ? chart : chart.slice(
            Math.max(0, firstIdx - 4),
            Math.min(chart.length, lastIdx + 4)
        );

        return { obsChartData: cropped, totalObs: total };
    }, [data.project?.spanCountTimeSeries?.data]);

    useEffect(() => {
        const currentData = activeTab === TAB_TRACES ? tracesChartData : obsChartData;
        if (currentData.length > 0 && onDataExport && widgetId && widgetTitle) {
            onDataExport(widgetId, widgetTitle, currentData);
        }
    }, [activeTab, tracesChartData, obsChartData, onDataExport, widgetId, widgetTitle]);

    const timeTickFormatter = (value: string) => {
        const date = new Date(value);
        if (scale === "DAY") {
            return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric' });
        }
        return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', hour12: true });
    };

    return (
        <div css={containerCSS}>
            <div css={tabsHeaderCSS}>
                <button css={tabBtnCSS(activeTab === TAB_TRACES)} onClick={() => setActiveTab(TAB_TRACES)}>
                    Traces
                </button>
                <button css={tabBtnCSS(activeTab === TAB_OBSERVATIONS)} onClick={() => setActiveTab(TAB_OBSERVATIONS)}>
                    Observations
                </button>
            </div>

            <div css={chartHeaderCSS}>
                <div css={iconWrapperCSS(activeTab === TAB_TRACES ? "#6366f1" : "#06b6d4")}>
                    {activeTab === TAB_TRACES ? "" : ""}
                </div>
                <div css={bigNumberCSS}>
                    {activeTab === TAB_TRACES ? totalTraces : totalObs}
                </div>
                <div css={subtitleCSS}>
                    {activeTab === TAB_TRACES ? "Total requests" : "System observations"}
                </div>
            </div>

            <div css={chartContainerCSS}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={activeTab === TAB_TRACES ? tracesChartData : obsChartData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                    >
                        <defs>
                            <linearGradient id="colorTraces" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#4338ca" stopOpacity={0.85} />
                                <stop offset="95%" stopColor="#4338ca" stopOpacity={0.15} />
                            </linearGradient>
                            <linearGradient id="colorDefault" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#1e40af" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#1e40af" stopOpacity={0.15} />
                            </linearGradient>
                            <linearGradient id="colorDebug" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#0e7490" stopOpacity={0.75} />
                                <stop offset="95%" stopColor="#0e7490" stopOpacity={0.15} />
                            </linearGradient>
                            <linearGradient id="colorError" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#be123c" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#be123c" stopOpacity={0.15} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid {...defaultCartesianGridProps} vertical={false} strokeDasharray="3 3" opacity={0.3} />
                        <XAxis
                            {...defaultXAxisProps}
                            dataKey="timestamp"
                            tickFormatter={timeTickFormatter}
                            axisLine={false}
                            tick={{ fontSize: 10, fontWeight: 600, fill: "var(--ac-global-text-color-400)" }}
                        />
                        <YAxis
                            {...defaultYAxisProps}
                            axisLine={false}
                            tick={{ fontSize: 10, fontWeight: 600, fill: "var(--ac-global-text-color-400)" }}
                            tickFormatter={(x) => intShortFormatter(x)}
                        />
                        <Tooltip content={(props: any) => <CustomTooltip {...props} scale={scale} />} cursor={{ stroke: "var(--ac-global-color-primary)", strokeWidth: 2, strokeDasharray: "4 4" }} />
                        {activeTab === TAB_TRACES ? (
                            <Area
                                type="monotone"
                                dataKey="value"
                                name="Traces"
                                stroke="#4338ca"
                                strokeWidth={2.5}
                                fillOpacity={1}
                                fill="url(#colorTraces)"
                                dot={{ r: 4, fill: "#eab308", stroke: "#fff", strokeWidth: 2 }}
                                activeDot={{ r: 7, stroke: "#fff", strokeWidth: 2.5 }}
                            />
                        ) : (
                            <>
                                <Area
                                    type="monotone"
                                    dataKey="Default"
                                    name="Default"
                                    stackId="1"
                                    stroke="#1e40af"
                                    strokeWidth={2}
                                    fill="url(#colorDefault)"
                                    dot={{ r: 3, fill: "#eab308", stroke: "#fff", strokeWidth: 1.5 }}
                                    activeDot={{ r: 6, fill: "#fff", stroke: "#fff", strokeWidth: 3 }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="Debug"
                                    name="Debug"
                                    stackId="1"
                                    stroke="#0e7490"
                                    strokeWidth={2}
                                    fill="url(#colorDebug)"
                                    dot={{ r: 3, fill: "#eab308", stroke: "#fff", strokeWidth: 1.5 }}
                                    activeDot={{ r: 6, fill: "#fff", stroke: "#fff", strokeWidth: 3 }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="Error"
                                    name="Error"
                                    stackId="1"
                                    stroke="#be123c"
                                    strokeWidth={2}
                                    fill="url(#colorError)"
                                    dot={{ r: 3, fill: "#eab308", stroke: "#fff", strokeWidth: 1.5 }}
                                    activeDot={{ r: 6, fill: "#fff", stroke: "#fff", strokeWidth: 3 }}
                                />
                            </>
                        )}
                        <Legend {...defaultLegendProps} iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
